/**
 * ReportIssueDialog
 *
 * Mobile-friendly dialog the driver opens from the DriverView header to
 * report a truck/chassis/container issue.
 */
import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Wrench, Truck, Package, AlertTriangle, Loader2, Camera, X as XIcon, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const CATEGORIES = [
  { value: 'truck', label: 'Truck', icon: Truck },
  { value: 'chassis', label: 'Chassis', icon: Wrench },
  { value: 'container', label: 'Container', icon: Package },
  { value: 'trailer', label: 'Trailer', icon: Truck },
  { value: 'other', label: 'Other', icon: AlertTriangle },
];

const SEVERITIES = [
  { value: 'low', label: 'Low — can keep working', color: 'text-green-400' },
  { value: 'medium', label: 'Medium — needs attention', color: 'text-yellow-400' },
  { value: 'high', label: 'High — should fix soon', color: 'text-orange-400' },
  { value: 'critical', label: 'Critical — unsafe to drive', color: 'text-red-400' },
];

export const ReportIssueDialog = ({ open, onOpenChange, onReported }) => {
  const { api } = useAuth();
  const [category, setCategory] = useState('truck');
  const [severity, setSeverity] = useState('medium');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [equipmentLabel, setEquipmentLabel] = useState('');
  const [containerNumber, setContainerNumber] = useState('');
  const [location, setLocation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Photos: array of {id, url, previewUrl, uploading}
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const reset = () => {
    setCategory('truck'); setSeverity('medium'); setTitle('');
    setDescription(''); setEquipmentLabel(''); setContainerNumber(''); setLocation('');
    setPhotos([]);
  };

  const onFilesPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (photos.length + files.length > 5) {
      toast.error('Max 5 photos per issue');
      return;
    }
    setUploading(true);
    const newPhotos = [];
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const previewUrl = URL.createObjectURL(file);
      newPhotos.push({ id: localId, previewUrl, uploading: true });
    }
    setPhotos((prev) => [...prev, ...newPhotos]);

    const uploadPromises = files.map(async (file, i) => {
      const localPhoto = newPhotos[i];
      try {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post('/equipment-issues/photos/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setPhotos((prev) => prev.map((p) =>
          p.id === localPhoto.id ? { ...p, id: data.id, url: data.url, uploading: false } : p
        ));
      } catch (err) {
        setPhotos((prev) => prev.filter((p) => p.id !== localPhoto.id));
        toast.error(`Upload failed: ${file.name}`);
      }
    });
    await Promise.all(uploadPromises);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePhoto = (id) => {
    setPhotos((prev) => prev.filter((p) => p.id !== id));
  };

  const submit = async () => {
    if (!title.trim()) {
      toast.error('Add a short title for the issue');
      return;
    }
    if (uploading) {
      toast.error('Wait for photos to finish uploading');
      return;
    }
    setSubmitting(true);
    try {
      const photo_urls = photos.filter((p) => p.url).map((p) => p.url);
      const payload = {
        category, severity,
        title: title.trim(),
        description: description.trim(),
        equipment_label: equipmentLabel.trim() || null,
        container_number: containerNumber.trim() || null,
        location: location.trim() || null,
        photo_urls,
      };
      await api.post('/equipment-issues/report', payload);
      toast.success('Issue reported — your team has been notified');
      reset();
      onOpenChange(false);
      if (onReported) onReported();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md" data-testid="report-issue-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-orange-400" />
            Report an Issue
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          {/* Category quick chips */}
          <div className="grid grid-cols-5 gap-1">
            {CATEGORIES.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => setCategory(value)}
                data-testid={`issue-category-${value}`}
                className={`flex flex-col items-center justify-center gap-1 py-2 rounded-lg border transition-colors text-xs ${
                  category === value
                    ? 'bg-blue-600 border-blue-500 text-white'
                    : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>

          {/* Severity */}
          <div className="space-y-1">
            <Label className="text-xs text-slate-400">Severity</Label>
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger data-testid="issue-severity-select" className="bg-slate-950 border-slate-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                {SEVERITIES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    <span className={s.color}>{s.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Title */}
          <div className="space-y-1">
            <Label className="text-xs text-slate-400">Title *</Label>
            <Input
              data-testid="issue-title-input"
              placeholder="e.g. Air leak in front brake chamber"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-slate-950 border-slate-800"
              maxLength={200}
            />
          </div>

          {/* Description */}
          <div className="space-y-1">
            <Label className="text-xs text-slate-400">What&apos;s wrong?</Label>
            <Textarea
              data-testid="issue-description-input"
              placeholder="Describe what happened, when, and any sounds/sensations…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-slate-950 border-slate-800 min-h-[80px]"
              maxLength={2000}
            />
          </div>

          {/* Equipment + container */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs text-slate-400">Equipment</Label>
              <Input
                data-testid="issue-equipment-input"
                placeholder="Truck #234"
                value={equipmentLabel}
                onChange={(e) => setEquipmentLabel(e.target.value)}
                className="bg-slate-950 border-slate-800"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-400">Container #</Label>
              <Input
                data-testid="issue-container-input"
                placeholder="ABCD1234567"
                value={containerNumber}
                onChange={(e) => setContainerNumber(e.target.value.toUpperCase())}
                className="bg-slate-950 border-slate-800 font-mono"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-slate-400">Where</Label>
            <Input
              data-testid="issue-location-input"
              placeholder="Long Beach yard, en route, customer site…"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="bg-slate-950 border-slate-800"
            />
          </div>

          {/* Photos */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-slate-400">Photos ({photos.length}/5)</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || photos.length >= 5}
                className="border-slate-700 h-7 text-xs"
                data-testid="issue-add-photos-btn"
              >
                {uploading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Camera className="h-3 w-3 mr-1" />}
                Add photos
              </Button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              capture="environment"
              onChange={onFilesPicked}
              className="hidden"
              data-testid="issue-photo-file-input"
            />
            {photos.length > 0 && (
              <div className="grid grid-cols-5 gap-1.5">
                {photos.map((p) => (
                  <div
                    key={p.id}
                    className="relative aspect-square bg-slate-950 border border-slate-800 rounded overflow-hidden"
                    data-testid={`issue-photo-${p.id}`}
                  >
                    {p.previewUrl ? (
                      <img src={p.previewUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-500">
                        <ImageIcon className="h-4 w-4" />
                      </div>
                    )}
                    {p.uploading && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <Loader2 className="h-4 w-4 animate-spin text-white" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removePhoto(p.id)}
                      className="absolute top-0.5 right-0.5 bg-black/70 hover:bg-black/90 rounded-full p-0.5 text-white"
                      data-testid={`issue-photo-remove-${p.id}`}
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline" onClick={() => onOpenChange(false)}
            disabled={submitting} className="border-slate-700 text-slate-300"
          >
            Cancel
          </Button>
          <Button
            onClick={submit} disabled={submitting || !title.trim()}
            className="bg-orange-600 hover:bg-orange-500"
            data-testid="issue-submit-btn"
          >
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Reporting…</> : 'Report'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReportIssueDialog;
